<script lang="ts" setup>
import { formatNumber } from 'cesg-shared';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  useAtaTableInvoicingOkLogic,
  type UseAtaTableInvoicingOkLogicProps,
} from './logic/useAtaTableInvoicingOkLogic';

const props = defineProps<UseAtaTableInvoicingOkLogicProps>();
const { state, action } = useAtaTableInvoicingOkLogic(props);
const { t } = useI18n();

const totalAmount = computed(() =>
  state.atas.reduce((sum, ata) => sum + (ata.amount ?? 0), 0),
);

function formatDate(date?: Date): string {
  if (!date) return '–';
  return date.toLocaleDateString('sv-SE');
}
</script>

<template>
  <div class="card">
    <div class="card-header">
      <p class="card-header-title">{{ t('ata.table.invoicingOkTitle') }}</p>
    </div>
    <div class="card-content p-0">
      <table class="table is-fullwidth is-striped is-hoverable mb-0">
        <thead>
          <tr>
            <th class="col-number">#</th>
            <th>{{ t('ata.table.name') }}</th>
            <th class="col-type">{{ t('ata.table.type') }}</th>
            <th class="col-date">{{ t('ata.table.doneDate') }}</th>
            <th class="col-amount">{{ t('ata.table.amount') }}</th>
            <th class="col-comment">{{ t('ata.table.comment') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="ata in state.atas" :key="ata.number">
            <td class="col-number ata-number" @click="action.select(ata)">
              {{ t('ata.nameSerialNr', { serialNumber: ata.number }) }}
            </td>
            <td>{{ ata.name }}</td>
            <td class="col-type">
              <span class="type-badge" :data-type="ata.type">{{
                ata.type
              }}</span>
            </td>
            <td class="col-date">{{ formatDate(ata.doneDate) }}</td>
            <td class="col-amount">{{ formatNumber(ata.amount) }} kr</td>
            <td class="col-comment muted">{{ ata.comment || '–' }}</td>
          </tr>
        </tbody>
        <tfoot>
          <tr class="summary-row">
            <td colspan="4">{{ t('sum') }}</td>
            <td class="col-amount">{{ formatNumber(totalAmount) }} kr</td>
            <td class="col-comment"></td>
          </tr>
        </tfoot>
      </table>
    </div>
  </div>
</template>

<style scoped>
.card-header {
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
}

.table th,
.table td {
  padding: 0.75rem 1rem;
}

.col-number {
  width: 6rem;
}

.col-type {
  width: 7rem;
}

.col-date {
  width: 10rem;
}

.col-amount {
  width: 11rem;
  text-align: right !important;
}

.col-comment {
  min-width: 10rem;
}

tfoot .summary-row td {
  font-weight: 700;
  border-top: 2px solid #ddd;
}

.ata-number {
  cursor: pointer;
  color: var(--primary-color);
  font-weight: 600;
}

.ata-number:hover {
  text-decoration: underline;
}

.muted {
  color: #888;
  font-size: 0.85rem;
}

.type-badge {
  display: inline-block;
  padding: 0.2rem 0.6rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 700;
  background: #e0e0e0;
  color: #222;
  letter-spacing: 0.03em;
}

@media (max-width: 900px) {
  .col-type {
    display: none;
  }

  .col-date {
    width: 6rem;
  }
}

@media (max-width: 600px) {
  .col-comment {
    display: none;
  }
}
</style>
